/**src\presentation\context\AuthContext\AuthProvider.tsx
 * Auth Provider Implementation
 *
 * Provides authentication context to the application.
 * Manages user state and authentication operations.
 */

import { useState, useEffect, type ReactNode } from 'react';
import { AuthContext, type AuthContextType } from './AuthContext';
import type { User } from '@domain/entities';
import { container } from '@di/container';
import type { IAuthService } from '@domain/services';
import type { CreateUserUseCase } from '@application/use-cases';
import { CreateUserDTO } from '@/application';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const authService = container.get<IAuthService>('IAuthService');
  const createUserUseCase =
    container.get<CreateUserUseCase>('CreateUserUseCase');

  useEffect(() => {
    // Initialize auth state on mount
    const initAuth = async () => {
      try {
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [authService]);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const result = await authService.login({ email, password });
      if (result.success) {
        setUser(result.user);
      }
      return result;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const createUser = async (input: CreateUserDTO) => {
    setIsLoading(true);
    try {
      const createdUser = await createUserUseCase.execute(input);
      setUser(createdUser);
      return createdUser;
    } catch (error) {
      console.error('Create user error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await authService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Refresh user error:', error);
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    createUser,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
